/**
 * #605 — the picker's answering controls take no input for a moment after
 * they appear: a click or key press the app timed for that spot, or one meant
 * for the app a moment earlier (a double-click, a held Enter), must not answer
 * for the person. It carries more weight where the engine cannot report an
 * activation (Firefox): the gate there rests on the app frame's own last
 * input, so this is what stops one press both opening the picker and
 * answering it.
 *
 * And a burst holds them longer: while they are still unarmed, every press
 * starts the moment over. A person clicking fast in the app (a game, a "click
 * here" page) when the picker appears under their pointer is still in that
 * burst — the controls arm only after a quiet moment. Once armed, presses no
 * longer count: a double-click, or a tick then Confirm, is the person using
 * the picker.
 */
import { useEffect, useState } from 'react'

/** How long after appearing — and after the last press of a burst — the
 *  answering controls take input. */
export const ARM_MS = 600

/** False for `ms` after mount, and for `ms` after any press that lands while
 *  still false (watched at the window, capture); then true for good. */
export function useArmed(ms = ARM_MS): boolean {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    let armedNow = false
    const arm = () => { armedNow = true; setArmed(true) }
    let timer = setTimeout(arm, ms)
    const onPress = () => {
      if (armedNow) return
      clearTimeout(timer)
      timer = setTimeout(arm, ms)
    }
    // POINTER only, deliberately. A keystroke must never hold the pointer path
    // hostage: the save picker autofocuses its name field with the stem
    // selected, so typing a name — faster than this window, as people type —
    // would keep Save disabled for the whole session, and a burst of keys
    // could extend the lockout indefinitely with nothing on screen saying why.
    // The keyboard's own rule lives in `useTypingCarry` (keys carried over
    // from the app do not answer), which correctly does not gate a click.
    window.addEventListener('pointerdown', onPress, true)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('pointerdown', onPress, true)
    }
  }, [ms])
  return armed
}
